import React, { useCallback } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import {
  trackClickEditRecipeQuantity,
  trackSelectPortionCount,
  trackUpdatePortion,
} from '@features/recipe-tile'
import type { MenuPreferences } from '@library/api-menu-experience'
import type { MenuRecipeTileRequestParams, Recipe, RecipeUUID } from '@library/menu-service'

import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { useStaticArray } from 'hooks/useStaticArray'
import { MENU_RECIPE_LIST_SKIP_TARGET_ID } from 'routes/Menu/accessibility'
import { selectRecipeVariant, showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { CaloriesTutorial } from 'routes/Menu/components/MenuPreferences'
import { useBasket } from 'routes/Menu/domains/basket'
import { useCollectionRecipes, useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { useFlexRecipesWeb } from 'routes/Menu/hooks/useFlexRecipesWeb'
import { getIsAuthenticated } from 'selectors/auth'

import { CTAToAllRecipes } from '../CTAToAllRecipes'
import { RecipeTile } from './RecipeTileWrapper'
import { useCustomisationEducation } from './useCustomisationEducation'
import { useTrackRecipeListEffect } from './useTrackRecipeListEffect'
import { useTrackSoldOutRecipesEffect } from './useTrackSoldOutRecipesEffect'

import css from './RecipeList.css'

export type RefreshRecipeTile = (
  recipeId: RecipeUUID,
  numPortions: MenuRecipeTileRequestParams['numPortions'],
) => Promise<void>

type UpdateRecipePortions = (
  recipeId: RecipeUUID,
  numPortions: MenuRecipeTileRequestParams['numPortions'],
) => void

export type RecipeListProps = {
  menuPreferences?: MenuPreferences
  refreshRecipeTile?: RefreshRecipeTile
}

export const RecipeList = ({ menuPreferences, refreshRecipeTile }: RecipeListProps) => {
  const currentCollectionId = useCurrentCollectionId()
  const isFlexRecipesWebEnabled = useFlexRecipesWeb()
  const isAuthenticated = useSelector(getIsAuthenticated)
  const { menuId } = useMenuMeta()

  const recipeSets = useCollectionRecipes(currentCollectionId)

  const recipesForSoldOutTracking = useStaticArray(
    recipeSets.map(({ recipe }) => recipe),
    (currentRecipe, nextRecipe) =>
      currentRecipe.id === nextRecipe.id && currentRecipe.isAvailable === nextRecipe.isAvailable,
  )
  const recipeIdsForListTracking = useStaticArray(recipeSets.map(({ recipe }) => recipe.id))
  useTrackSoldOutRecipesEffect(recipesForSoldOutTracking)
  useTrackRecipeListEffect(currentCollectionId, recipeIdsForListTracking)

  const dispatch = useDispatch()

  const {
    canAddRecipes,
    addRecipeByUuid,
    getQuantitiesForRecipeId,
    getNumPortionsForRecipeId,
    removeRecipeByUuid,
    recipeCount,
    reachedLimit,
    updateRecipePortionsByUuid,
  } = useBasket()

  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()

  const onClick = useCallback(
    (recipeUuid: RecipeUUID) => dispatch(showDetailRecipe(recipeUuid)),
    [dispatch],
  )
  const selectVariant = useCallback(
    ({
      variant,
      variantOutOfStock,
      originalRecipeId,
      recipeReference,
    }: {
      variant: Recipe
      variantOutOfStock: boolean
      originalRecipeId: RecipeUUID
      recipeReference: string
    }) => {
      dispatch(
        selectRecipeVariant({
          originalRecipeId,
          variant,
          collectionId: currentCollectionId,
          variantOutOfStock,
          view: 'grid',
          close: true,
          recipeReference,
        }),
      )
    },
    [currentCollectionId, dispatch],
  )

  const { recipeIdForEducation, closeEducation } = useCustomisationEducation(recipeSets)

  const updateRecipeTilePortions = useCallback<UpdateRecipePortions>(
    (recipeId, numPortions) => {
      updateRecipePortionsByUuid(recipeId, numPortions)
      void refreshRecipeTile?.(recipeId, numPortions)
    },
    [refreshRecipeTile, updateRecipePortionsByUuid],
  )

  return (
    <div className={css.recipeListWrapper}>
      <div className={css.threeTileRecipeList} id={MENU_RECIPE_LIST_SKIP_TARGET_ID} tabIndex={-1}>
        {recipeSets.map((value, index: number) => {
          const recipeNumPortions = getNumPortionsForRecipeId(value.recipe.id)
          const flexPortionTrackingContext = {
            basketRecipeCount: recipeCount,
            menuUuid: menuId,
            recipePortionCount: recipeNumPortions,
            recipeUuid: value.recipe.id,
            source: 'view_recipe_list_screen' as const,
          }

          return (
            <React.Fragment key={value.reference || value.recipe.id || `recipe-${index}`}>
              {index === 5 && <CaloriesTutorial />}
              <RecipeTile
                recipe={value.recipe}
                currentCollectionId={currentCollectionId}
                menuId={menuId}
                onClick={onClick}
                menuPreferences={menuPreferences}
                showCustomisedTileFooter={isAuthenticated}
                showFlexPortionTileBadge={isFlexRecipesWebEnabled}
                setBrowserCTAVisible={setBrowserCTAVisible}
                canAddRecipes={canAddRecipes}
                addRecipe={addRecipeByUuid}
                removeRecipe={removeRecipeByUuid}
                reachedLimit={reachedLimit}
                selectVariant={selectVariant}
                recipeReference={value.reference}
                originalId={value.originalId}
                index={index + 1}
                quantity={getQuantitiesForRecipeId(value.recipe.id)}
                onClickEditPortions={() => trackClickEditRecipeQuantity(flexPortionTrackingContext)}
                onSelectPortions={(numPortions) =>
                  trackSelectPortionCount(flexPortionTrackingContext, numPortions)
                }
                showCustomisationEducation={value.recipe.id === recipeIdForEducation}
                closeCustomisationEducation={closeEducation}
                onUpdateRecipePortions={(recipeId, updatedNumPortions) => {
                  updateRecipeTilePortions(recipeId, updatedNumPortions)
                  trackUpdatePortion(flexPortionTrackingContext, updatedNumPortions)
                }}
              />
            </React.Fragment>
          )
        })}
        <CTAToAllRecipes />
      </div>
    </div>
  )
}
